///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2020
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useRef, useContext } from "react";
import { useDay } from "@datepicker-react/hooks";
import styled from "styled-components";
import { isEqual } from "date-fns";
import DatepickerContext from "./datePickerContext";
import getColor from "../../date-picker-utils/getColor";
import blueGrey from "@material-ui/core/colors/blueGrey";
import grey from "@material-ui/core/colors/grey";
import blue from "@material-ui/core/colors/blue";

const DayContainer = styled.span`
  font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
  border: 0;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  align-self: center;
  background: ${(props) =>
    props.isSameDate
      ? props.getColorFn({
          rangeHoverColor: "#FFFFFF",
          selectedFirstOrLastColor: "#FFFFFF",
          selectedColor: "#FFFFFF",
        })
      : props.getColorFn({
          rangeHoverColor: blueGrey[100],
          selectedFirstOrLastColor: blueGrey[100],
          selectedColor: blueGrey[100],
        })};
  ${(props) => {
    if (props.isSelectedStartOrEnd) {
      if (props.isEndDate) {
        return "border-top-right-radius: 30px; border-bottom-right-radius: 30px;";
      } else {
        return "border-top-left-radius: 30px; border-bottom-left-radius: 30px;";
      }
    }
  }}
`;

const DayButton = styled.button`
  font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
  display: block;
  padding: 4px;
  outline: 0;
  border: 0;
  border-radius: 30px;
  align-self: top;
  width: 32px;
  height: 32px;
  font-size: 12px;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "cursor")};
  color: ${(props) =>
    props.getColorFn({
      selectedFirstOrLastColor: "#000",
      normalColor: "#000",
      selectedColor: "#000",
      rangeHoverColor: "#000",
      disabledColor: grey[500],
      todayColor: blue[500],
    })};
  background: ${(props) =>
    props.getColorFn({
      selectedFirstOrLastColor: blueGrey[300],
      normalColor: "#FFFFFF",
      selectedColor: blueGrey[100],
      rangeHoverColor: blueGrey[100],
      disabledColor: "#FFFFFF",
      todayColor: "#FFFFFF",
    })};
  :hover {
    background: ${blueGrey[300]};
    color: ${(props) =>
      props.getColorFn({
        selectedFirstOrLastColor: "#000",
        normalColor: "#000",
        selectedColor: "#000",
        rangeHoverColor: "#000",
        disabledColor: grey[500],
        todayColor: "#000",
      })};
  }
`;

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const Day = ({ dayLabel, date }) => {
  const dayRef = useRef(null);
  const {
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
    startDate,
    endDate,
  } = useContext(DatepickerContext);
  const {
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    disabledDate,
    onClick,
    onKeyDown,
    onMouseEnter,
    tabIndex,
  } = useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    dayRef,
  });

  const isDateTodayColor =
    isToday(date) &&
    !isSelectedStartOrEnd &&
    !isWithinHoverRange &&
    !isSelected;
  const getColorFn = getColor(
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    disabledDate,
    isDateTodayColor
  );
  const isEndDate = date > startDate && isSelectedStartOrEnd;
  const isSameDate = isEqual(startDate, endDate);
  if (!dayLabel) {
    return <div />;
  }

  return (
    <DayContainer
      getColorFn={getColorFn}
      isEndDate={isEndDate}
      isSelectedStartOrEnd={isSelectedStartOrEnd}
      isSameDate={isSameDate}
    >
      <DayButton
        onClick={onClick}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        tabIndex={tabIndex}
        type="button"
        ref={dayRef}
        getColorFn={getColorFn}
        isSelectedStartOrEnd={isSelectedStartOrEnd}
        isDateTodayColor={isDateTodayColor}
        isDisabled={disabledDate}
      >
        {dayLabel}
      </DayButton>
    </DayContainer>
  );
};

export default Day;
